// Idle timeout utility for external site
// Redirects to login page after 60 minutes of inactivity

class IdleTimeout {
  constructor(timeoutMinutes = 5, redirectUrl = '/') {
    this.timeoutMinutes = timeoutMinutes;
    this.redirectUrl = redirectUrl;
    this.timeoutId = null;
    this.lastActivity = Date.now();
    this.isActive = false;
    
    // Bind methods
    this.resetTimer = this.resetTimer.bind(this);
    this.handleActivity = this.handleActivity.bind(this);
    this.start = this.start.bind(this);
    this.stop = this.stop.bind(this);
    this.checkSession = this.checkSession.bind(this);
  }

  // Reset the idle timer
  resetTimer() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    
    this.lastActivity = Date.now();
    this.timeoutId = setTimeout(() => {
      this.handleTimeout();
    }, this.timeoutMinutes * 60 * 1000);
  }

  // Handle user activity
  handleActivity() {
    if (this.isActive) {
      this.resetTimer();
      // Extend the server session
      this.extendSession();
    }
  }

  // Extend server session
  async extendSession() {
    try {
      await fetch('/api/extendSession', {
        method: 'GET',
        credentials: 'include'
      });
    } catch (error) {
      console.log('Failed to extend session:', error);
    }
  }

  // Handle timeout - redirect to login
  handleTimeout() {
    console.log('Session expired due to inactivity');
    
    // Clear any stored session data
    if (typeof window !== 'undefined') {
      // Clear any localStorage/sessionStorage if needed
      sessionStorage.clear();
      
      // Show a message to the user
      alert('Your session has expired due to inactivity. You will be redirected to the login page.');
      
      // Redirect to login page
      window.location.href = this.redirectUrl;
    }
  }

  // Check if session is still valid
  async checkSession() {
    try {
      const response = await fetch('/api/getextuser', {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Session expired');
      }
      
      const data = await response.json();
      if (!data.loggedin) {
        throw new Error('Not logged in');
      }
      
      return true;
    } catch (error) {
      console.log('Session check failed:', error);
      this.handleTimeout();
      return false;
    }
  }

  // Start monitoring for idle activity
  start() {
    if (this.isActive) {
      return;
    }
    
    this.isActive = true;
    this.resetTimer();
    
    // Add event listeners for user activity
    const events = [
      'mousedown', 'keypress', 'scroll', 'touchstart', 'click'
    ];
    
    events.forEach(event => {
      document.addEventListener(event, this.handleActivity, true);
    });
    
    // Check session validity every 5 minutes
    this.sessionCheckInterval = setInterval(() => {
      this.checkSession();
    }, 5 * 60 * 1000);
    
    console.log(`Idle timeout started - ${this.timeoutMinutes} minutes`);
  }

  // Stop monitoring
  stop() {
    if (!this.isActive) {
      return;
    }
    
    this.isActive = false;
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
    
    // Remove event listeners
    const events = [
      'mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'
    ];
    
    events.forEach(event => {
      document.removeEventListener(event, this.handleActivity, true);
    });
    
    console.log('Idle timeout stopped');
  }

  // Get time remaining before timeout
  getTimeRemaining() {
    const elapsed = Date.now() - this.lastActivity;
    const remaining = (this.timeoutMinutes * 60 * 1000) - elapsed;
    return Math.max(0, remaining);
  }

  // Get minutes remaining
  getMinutesRemaining() {
    return Math.ceil(this.getTimeRemaining() / (60 * 1000));
  }
}

export default IdleTimeout; 
